import { FormattedEvent } from '@eventbrite/event-renderer';
import { $FixMe } from '@eventbrite/ts-utils';
import React, { FunctionComponent, useContext } from 'react';
import { connect } from 'react-redux';

import { trackEventClickInternal as trackEventClickInternalAction } from '../../../../../../redux/actions/analytics';
import { setHoverEventTarget as setHoverEventTargetAction } from '../../../../../../redux/actions/ui';
import { TrackingContext } from '../../../../../../trackingContext';

import { useEnvContext } from '../../../../../../context';
import { useInitialEventsExpansionQueryState } from '../../../../SearchPageDataExpansions';
import { ReportAdModalProps } from '../../../PromotedActionsMenuEventCard/ReportAdModal';
import { SearchResultsPanelAdsUpsell } from '../SearchResultPanelAdsUpsell';
import { SearchResultPanelContentEventCard } from '../SearchResultPanelContentEventCard';
import { useSearchAdsUpsellExperiment } from './SearchResultPanelContentEventCardExperiments';
import styles from './SearchResultPanelContentEventCardList.module.scss';

type SearchResultsPanelContentEventCardListProps = Pick<
    ReportAdModalProps,
    'getTrackingContext'
> & {
    events: FormattedEvent[];
    locale: string;
};

type SearchResultsPanelContentEventCardListReduxProps = {
    isAuthenticated?: boolean;
    shouldShowDebugOutput?: boolean;
    setHoverEventTarget: Function;
    focusedEvents: string[];
    raisedEvents: string[];
    // Tracking functions
    onTrackEventClickInternal: Function;
};

export const SearchResultPanelContentEventCardListInternal = ({
    events = [],
    isAuthenticated = false,
    onTrackEventClickInternal,
    shouldShowDebugOutput,
    setHoverEventTarget,
    focusedEvents,
    raisedEvents,
    getTrackingContext,
}: SearchResultsPanelContentEventCardListProps &
    SearchResultsPanelContentEventCardListReduxProps) => {
    const { locale } = useContext(TrackingContext);
    const expansionStatus = useInitialEventsExpansionQueryState();

    const eventCards = events.map((event: FormattedEvent, index: number) => (
        <li key={`${event.id}-${event.isPromoted}`}>
            <SearchResultPanelContentEventCard
                isAuthenticated={isAuthenticated}
                onTrackEventClickInternal={onTrackEventClickInternal}
                locale={locale}
                shouldShowDebugOutput={shouldShowDebugOutput}
                setHoverEventTarget={setHoverEventTarget}
                scrollId={focusedEvents[0]}
                isExpansionsLoading={
                    expansionStatus === undefined ||
                    expansionStatus === 'loading'
                }
                getTrackingContext={getTrackingContext}
                position={index + 1}
                isLazyImage={index > 3}
                isRaised={
                    focusedEvents.includes(event.id) ||
                    raisedEvents.includes(event.id)
                }
                event={event}
            />
        </li>
    ));

    const eventCardsWithUpsells = useEventCardsWithUpsells(eventCards, events);

    return <ul className={styles.eventList}>{eventCardsWithUpsells}</ul>;
};

function useEventCardsWithUpsells(
    eventCards: JSX.Element[],
    events: FormattedEvent[],
) {
    const { user } = useEnvContext();
    const isExperimentEnabled = useSearchAdsUpsellExperiment();
    const promotedEvents = events.filter((event) => event.isPromoted);
    const hasNoPromotedEvents = promotedEvents.length === 0;

    if (!isExperimentEnabled || hasNoPromotedEvents || !user?.orgHasEvents) {
        return eventCards;
    }

    // For now, promoted events are always the first N events
    const lastPromotedIndex = promotedEvents.length;
    const eventCardsWithUpsells = [
        eventCards.slice(0, lastPromotedIndex),
        <li key="promoted-events-upsell">
            <SearchResultsPanelAdsUpsell data-testid="promoted-events-upsell" />
        </li>,
        eventCards.slice(lastPromotedIndex),
    ];

    return eventCardsWithUpsells;
}

export const _mapStateToProps = (
    state: any,
    ownProps: SearchResultsPanelContentEventCardListProps,
) => {
    return {
        isAuthenticated: state.auth.isAuthenticated,
        shouldShowDebugOutput: state.admin.shouldShowDebugOutput,
        focusedEvents: state.ui.focusedEventCards,
        raisedEvents: state.ui.raisedEventCards,
        ...ownProps,
    };
};

const _mapDispatchToProps = (dispatch: Function) => ({
    setHoverEventTarget: (...args: [$FixMe]) =>
        dispatch(setHoverEventTargetAction(...args)),
    onTrackEventClickInternal: (...args: [$FixMe]) =>
        dispatch(trackEventClickInternalAction(...args)),
});

export const SearchResultPanelContentEventCardList: FunctionComponent<SearchResultsPanelContentEventCardListProps> =
    connect(
        _mapStateToProps,
        _mapDispatchToProps,
    )(SearchResultPanelContentEventCardListInternal);
